import { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
} from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import ProjectSteps, { PROJECT_STEPS, ProjectStepKey } from "./ProjectSteps";
import ProjectDeliverables from "./ProjectDeliverables";
import DocumentAI from "./DocumentAI";
import ComplianceEngine from "./ComplianceEngine";
import Findings from "./Findings";
import { exportFindings } from "./api";
import {
  ComplianceRun,
  EMPTY_STATS,
  ProjectStats,
  STATUS_LABELS,
  STATUS_TONE,
  VerityProject,
  deriveProjectProgress,
  reviewersOf,
} from "./types";

interface ProjectWorkspaceProps {
  project: VerityProject;
  onBack: () => void;
  /**
   * Opened from the admin shell.
   *
   * Gates what is the administrator's rather than the reviewer's: the stages below become
   * read-only, because an administrator follows a project, they do not do its work. The audit
   * trail is reached from the project LIST rather than from in here — reading the record of a
   * review is not a step in doing one.
   */
  admin?: boolean;
}

/**
 * Which stages have actually produced something.
 *
 * ONE definition, because three things read it: the green tick on the tracker, whether the way
 * forward is lit, and which stage the project opens on. The first two were separate once and said
 * different things — Deliverables gated on a parse while every later stage handed you a solid button
 * regardless, so Document AI offered "Compliance Engine" in full colour beside its own message
 * saying nothing had been parsed yet. Derived from one predicate they cannot drift.
 */
function doneStepsFor(stats: ProjectStats, run: ComplianceRun | null): ProjectStepKey[] {
  return [
    // One parsed document is enough: Document AI has something to show.
    ...(stats.parsed > 0 ? (["deliverables"] as ProjectStepKey[]) : []),
    // Document AI is finished when there is nothing left unread.
    ...(stats.deliverables > 0 && stats.parsed >= stats.deliverables
      ? (["document-ai"] as ProjectStepKey[])
      : []),
    ...(run ? (["compliance"] as ProjectStepKey[]) : []),
    // Findings is finished only when every one of them has been ruled on.
    ...(run?.findings.length && run.findings.every((f) => (f.status ?? "open") !== "open")
      ? (["findings"] as ProjectStepKey[])
      : []),
  ];
}

/**
 * The stage to open on: the one after the furthest that has produced something.
 *
 * Takes the LAST done stage rather than the first gap, so a project that somehow has a compliance
 * run but no parse count still opens at the far end rather than sending you back to the start.
 * A project with nothing behind it opens on Deliverables, which is where the work begins.
 */
function openingStep(done: ProjectStepKey[]): ProjectStepKey {
  let furthest = -1;
  PROJECT_STEPS.forEach((s, i) => {
    if (done.includes(s.key)) furthest = i;
  });
  return PROJECT_STEPS[Math.min(furthest + 1, PROJECT_STEPS.length - 1)].key;
}

/**
 * One project, opened.
 *
 * The header and the step tracker are fixed; the step below them changes. Deliverables is the only
 * stage with anything behind it — the rest carry a placeholder rather than a stub UI, so nobody
 * mistakes an empty screen for a screen that ran and found nothing.
 */
const ProjectWorkspace = ({ project, onBack, admin = false }: ProjectWorkspaceProps) => {
  /**
   * Opens where the work actually is, not back at the beginning.
   *
   * Reopening a project whose documents are all parsed and landing on Deliverables makes you retrace
   * a stage you finished to get to the one you came for. The list row already says "Next: Run
   * compliance"; this makes opening the project agree with it.
   *
   * Lazy initialiser, so it reads the row's counts ONCE at mount. Recomputing it as the live stats
   * arrive would drag you forward mid-task the moment a parse finished.
   */
  const [step, setStep] = useState<ProjectStepKey>(() =>
    openingStep(doneStepsFor(project.stats ?? EMPTY_STATS, project.complianceRun ?? null)),
  );
  /** Live counts from the deliverables stage — the same numbers the list derives its status from. */
  const [stats, setStats] = useState<ProjectStats>(project.stats ?? EMPTY_STATS);
  /** The deliverable the ⚡ was pressed on, so the next stage knows what it was sent. */
  const [focus, setFocus] = useState<{ id: string; fileName: string } | null>(null);

  /**
   * A run finished in this session, so the header flips without a reload.
   *
   * Always null today: the compliance route answers 501, so nothing can set it. It is wired anyway
   * because the two stages below take it, and because the moment there is an engine this is the
   * only thing that has to change here.
   */
  const [run, setRun] = useState<ComplianceRun | null>(project.complianceRun ?? null);
  /**
   * Has the reviewer been through the run's own result yet?
   *
   * A finished check reports what it did — how long it took, how much it could assess, how much it
   * could not — and that is the context every finding is read in. Landing straight on a list of
   * ninety items skips the one screen that says what the ninety are out of, so Findings stays shut
   * until "Review findings" is pressed on the Compliance Engine.
   *
   * ONLY FOR A RUN THAT JUST FINISHED. A project opened with a run already stored has had that
   * conversation; making somebody re-acknowledge a result from last week would be a checkpoint
   * with nothing behind it. So this starts true when the project arrives carrying a run, and false
   * when one completes while you are watching.
   */
  const [reviewOpened, setReviewOpened] = useState(() => Boolean(project.complianceRun));

  /** The "what these mean" note behind the Findings Review heading. */
  const [findingsHelp, setFindingsHelp] = useState(false);
  /** The export format picker, and which format is currently being built. */
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState<"xlsx" | "pdf" | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  /**
   * Builds the file and hands it to the browser.
   *
   * The work is the server's — it already holds every finding with its evidence and its decision —
   * so this is a fetch and a download, not a second rendering of what a finding means.
   */
  const runExport = async (format: "xlsx" | "pdf") => {
    setExportOpen(false);
    setExporting(format);
    setExportError(null);
    try {
      await exportFindings(project.id, format);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Could not build the report.");
    } finally {
      setExporting(null);
    }
  };

  const { status, progress } = deriveProjectProgress(stats, run?.runAt ?? project.complianceRunAt);

  const doneSteps = doneStepsFor(stats, run);

  // The stage after this one, and whether this one has produced what it needs to. The last stage has
  // nowhere to go, so it gets no button.
  const nextStep = PROJECT_STEPS[PROJECT_STEPS.findIndex((s) => s.key === step) + 1];
  // Findings is the one stage the forward button does not light for on its own: a fresh run has to
  // be read before its findings are, and "Review findings" on the result card is where that
  // happens. The button stays reachable — pressing it lands on the explanation, not on a dead end.
  const nextReady =
    doneSteps.includes(step) && !(nextStep?.key === "findings" && !reviewOpened);

  return (
    // Which project and which stage is the frame you work inside, so it is pinned and only the stage
    // below it scrolls — the Requirements workbench pattern.
    <div className="vr-page vr-page-fixed">
      <div className="vr-page-head">
        <div>
          <button className="vr-rowbtn" onClick={onBack} style={{ marginBottom: ".75rem" }}>
            <ArrowLeft size={12} /> Projects
          </button>
          {/* ON FINDINGS THE HEADING IS THE WORK, NOT THE PROJECT.
              Every other stage is a step you are taking on a named project, so the project's name
              belongs at the top. Findings Review is where the project's details stop being the
              frame and become one fact among several — which is why they move into the chain under
              the tracker and the stage takes the heading. */}
          {step === "findings" ? (
            <h1 className="vr-h-page">
              Findings Review
              <button
                type="button"
                className="vr-info-btn"
                aria-expanded={findingsHelp}
                title={findingsHelp ? "Hide" : "What these mean"}
                onClick={() => setFindingsHelp((open) => !open)}
              >
                i
              </button>
            </h1>
          ) : (
            <h1 className="vr-h-page">{project.name}</h1>
          )}
          <p style={{ marginTop: ".25rem" }} hidden={step === "findings" && !findingsHelp}>
            {step === "findings" ? (
              <>
                Breaches are requirements the submission does not meet. Unresolved ones could not be
                settled from the drawings' text and need an eye on the sheet. Accept a finding and it
                stands; dismiss it and say why.
              </>
            ) : (
              <>
                {project.projectRef ? `${project.projectRef} · ` : ""}
                {project.supplier}
                {project.location ? ` · ${project.location}` : ""}
                {/* Whose project this is. An administrator watching one needs to know who to ask. */}
                {reviewersOf(project).length > 0
                  ? ` · ${reviewersOf(project).length === 1 ? "Reviewer" : "Reviewers"}: ${reviewersOf(
                      project,
                    )
                      .map((r) => r.name)
                      .join(", ")}`
                  : ""}
              </>
            )}
          </p>
        </div>
        <div className="vr-head-actions">
          {/* Status and what to do next, derived from the work itself — never set by hand. */}
          <div className="vr-ws-status">
            <StatusBadge status={STATUS_TONE[status]} label={STATUS_LABELS[status]} />
            <span className="vr-cell-code">Next: {progress}</span>
          </div>

          {/* The run as a file somebody can send. Sits where every other stage puts its forward
              action, because on Findings that is what it is: the list has been read, this is what
              leaves with you. Two formats because they are used differently — the spreadsheet to
              work through, the PDF to send on. */}
          {step === "findings" && run && (
            <div className="vr-export">
              <button
                className="vr-btn vr-btn-outline"
                onClick={() => setExportOpen((open) => !open)}
                disabled={exporting !== null}
                aria-expanded={exportOpen}
              >
                {exporting ? (
                  <Loader2 size={15} className="vr-spin-inline" />
                ) : (
                  <Download size={15} />
                )}
                {exporting ? `Building ${exporting.toUpperCase()}…` : "Export findings"}
              </button>
              {exportOpen && !exporting && (
                <div className="vr-export-menu">
                  <button onClick={() => void runExport("xlsx")}>
                    <FileSpreadsheet size={14} /> Excel — one row per finding
                  </button>
                  <button onClick={() => void runExport("pdf")}>
                    <FileText size={14} /> PDF — a report to send
                  </button>
                </div>
              )}
            </div>
          )}

          {/* The way forward. It lights up once this stage has produced what the next one needs —
              muted before that, but never locked: the step tracker lets you move freely, and a
              button that refused to would contradict it. */}
          {nextStep && (
            <button
              className={`vr-btn ${nextReady ? "vr-btn-glow" : "vr-btn-outline"}`}
              onClick={() => setStep(nextStep.key)}
              title={`Go to ${nextStep.label}`}
            >
              {nextStep.label} <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>

      {exportError && <div className="vr-form-error">{exportError}</div>}

      <ProjectSteps
        active={step}
        onSelect={setStep}
        // A stage ticks only once it has produced something the next one can use. An uploaded file
        // is not yet a result, so Deliverables waits for a parse rather than for an upload.
        done={doneSteps}
        // Findings cannot hold anything until a compliance run has produced it, so it reads dimmed
        // until then — the same signal the Run Compliance Check button gives while its inputs are
        // unchosen. Still clickable: the stage explains itself and offers the way back.
        pending={run ? [] : (["findings"] as ProjectStepKey[])}
      />

      {/* Findings pins its own frame and scrolls only its list, so the region must stop being the
          scroller for that stage — two nested scrollers is how a page ends up with the inner one
          unreachable. Every other stage scrolls whole, as before. */}
      <div className={`vr-scroll-region${step === "findings" ? " vr-scroll-region-hosted" : ""}`}>
        {/* THE ADMIN WATCHES; THE REVIEWER WORKS. An administrator sets a project up, assigns it and
            follows it — they do not submit the documents, parse them or run the check. Two people
            able to do that against one project is how a pack gets submitted twice and reviewed
            once, and how "who ran this" stops having an answer. */}
        {step === "deliverables" && (
          <ProjectDeliverables
            project={project}
            readOnly={admin}
            onStats={setStats}
            onAdvance={(deliverable) => {
              setFocus({ id: deliverable.id, fileName: deliverable.fileName });
              setStep("document-ai");
            }}
          />
        )}

        {step === "document-ai" && (
          <DocumentAI project={project} focusId={focus?.id} readOnly={admin} />
        )}

        {step === "compliance" && (
          <ComplianceEngine
            project={project}
            run={run}
            readOnly={admin}
            onRun={(finished) => {
              setRun(finished);
              // A run that just finished has not been read yet, whatever was true on arrival.
              setReviewOpened(false);
            }}
            onViewFindings={() => {
              setReviewOpened(true);
              setStep("findings");
            }}
          />
        )}

        {step === "findings" && (
          <Findings
            project={project}
            run={run}
            gated={!reviewOpened}
            onRun={setRun}
            onRunCompliance={() => setStep("compliance")}
            onUploadRevision={() => setStep("deliverables")}
          />
        )}

      </div>

    </div>
  );
};

export default ProjectWorkspace;
